import React from 'react';
import { Link } from 'react-router-dom';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createColumnHelper, FilterFn, flexRender, getCoreRowModel, getFilteredRowModel, getSortedRowModel,
  Row, SortingState, useReactTable
} from '@tanstack/react-table';

import Badge from '../../../components/Library/Badge';
import DebouncedInput from '../../../components/Library/DebouncedInput';
import { getIconForSortDirection } from '../../../components/Library/getIconForSortDirection';
import CenterLoadingSpinner from '../../../components/LoadingSpinner/CenterLoadingSpinner';
import { useAccountContext } from '../../../contexts/AccountContext';
import { formatToLocalTime } from '../../../libs/libDate';
import { useQueryObjectState, useQueryState } from '../../../libs/useQueryState';
import { sortBy } from '../../../libs/utils';
import { User, UserScimInfo, useUsersApi } from '../../../middle_auth_api';
import { ISO1806_TIMESTAMP_REGEX } from '../../Systems/getDisplayValue';

const filter: FilterFn<any> = (row: Row<any>, columnId: string, filterValue: any): boolean => {
  const value = row.getValue(columnId);
  const filter = filterValue.toLowerCase();
  if (typeof value === 'number') {
    return value.toString().includes(filter);
  }
  if (typeof value === 'boolean') {
    return (value ? 'true' : 'false').includes(filter);
  }
  if (typeof value === 'string') {
    if (ISO1806_TIMESTAMP_REGEX.test(value)) {
      return formatToLocalTime(value).toLowerCase().includes(filter);
    }
    return value.toLowerCase().includes(filter);
  }
  return false;
};

const columnHelper = createColumnHelper<User>();

const columns = [
  columnHelper.accessor('display_name', {
    header: () => 'Display Name',
    cell: info => <><Link to={info.row.original.id} className='underline'>{info.getValue()}</Link></>
  }),
  columnHelper.accessor('email', {
    header: () => 'Email',
    cell: info => info.getValue() ?? <i>null</i>
  }),
  columnHelper.accessor('scim_info', {
    header: () => 'User Source',
    cell: info => (info.getValue() == null ? 'Platform' : <><Badge variant='secondary'>SCIM</Badge>{(info.row.original.scim_info as UserScimInfo).scim_integration.name}</>)
  }),
  columnHelper.accessor('modified_timestamp', {
    header: () => 'Last Modified At',
    cell: info => formatToLocalTime(info.getValue())
  }),
  columnHelper.accessor('modified_by.display_name', {
    header: () => 'Last Modified By',
    cell: info => <span>{info.getValue()}</span>
  })
]


const SettingsUsersList: React.FC = () => {
  const accountContext = useAccountContext();
  const usersApi = useUsersApi();
  const queryClient = useQueryClient();

  const [sorting, setSorting] = useQueryObjectState<SortingState>('sort', [{ desc: false, id: 'display_name' }])
  const [globalFilter, setGlobalFilter] = useQueryState('q', '');

  const usersQuery = useQuery(['settings', 'users', accountContext.accountId], async () => {
    const usersResponse = await usersApi.list_users();

    return usersResponse;
  }, {
    onSuccess(data) {
      data.results.forEach((user) => {
        queryClient.setQueryData(['settings', 'user', user.id], user);
      });
    },
  });

  const table = useReactTable({
    data: usersQuery.data?.results.sort(sortBy('display_name')) ?? [],
    columns,
    state: {
      sorting,
      globalFilter
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onSortingChange: setSorting,
    globalFilterFn: filter
  })

  if (usersQuery.data == null) {
    return <CenterLoadingSpinner />
  }

  return <>
    <div className='flex'>
      <div className='md:ml-auto'>
        <DebouncedInput value={globalFilter} onChange={setGlobalFilter} debounce={500} placeholder='Filter Data' className='inline-block' />
      </div>
    </div>
    <table className='w-full p-3'>
      <thead>
        {table.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id} className='border-b border-b-background3-dark'>
            {headerGroup.headers.map((header) => (
              <th key={header.id} className='py-3 text-left px-2'>
                {header.isPlaceholder
                  ? null
                  : <div onClick={header.column.getToggleSortingHandler()} className={`flex ${header.column.getCanSort() ? 'cursor-pointer select-none' : ''}`}>
                      <div className='inline-block'>{flexRender(header.column.columnDef.header, header.getContext())}</div>
                      <div className='inline-block ml-auto'>{getIconForSortDirection(header.column.getIsSorted())}</div>
                    </div>}
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody>
        {table.getRowModel().rows.map(row => (
          <tr key={row.id} className={`border-b border-b-background3-dark hover:bg-hover ${row.original.enabled ? '' : 'line-through text-'}`}>
            {row.getVisibleCells().map(cell => {
              return (
                <td key={cell.id} className='p-2 whitespace-nowrap overflow-ellipsis'>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              );
            })}
          </tr>
        ))}
        {usersQuery.data.count !== 0 && table.getRowModel().rows.length === 0 && <>
          <tr className='border-b border-b-background3-dark'>
            <td colSpan={columns.length} className='p-2 whitespace-nowrap overflow-ellipsis text-center'>
              No User(s) Match Your Filter
            </td>
          </tr>
        </>}
        {usersQuery.data.count === 0 && <>
          <tr className='border-b border-b-background3-dark'>
            <td colSpan={columns.length} className='p-2 whitespace-nowrap overflow-ellipsis text-center'>
              There are no user(s) for this Organization
            </td>
          </tr>
        </>}
      </tbody>
    </table>
    <div className='mt-4 mb-2'>
      <Link to='new' className='ml-1 mt-3 px-2 py-2 rounded-lg text-success-text bg-success hover:bg-success-dark shadow-md shadow-hover'>New</Link>
    </div>
  </>;
};

export default SettingsUsersList;